import React, { useMemo, useState } from 'react';
import { Card, Tabs, Segmented, Typography } from 'antd';
import Chart from 'react-apexcharts';
import { useQuery } from 'react-query';
import { getAppEventsChart } from '../../services/appEventsService';

const { TabPane } = Tabs;
const { Text } = Typography;

const EVENT_COLORS = {
  shop_installed: '#52c41a',
  shop_uninstalled: '#ff4d4f',
  shop_subscribed_upgrade: '#1677ff',
  shop_subscribed_downgrade: '#fa8c16',
  shop_unsubscribed: '#ff4d4f',
  shop_closed: '#fadb14',
  shop_reopened: '#722ed1',
  netGrow: '#1f1f1f',
  cumulativeGrow: '#13c2c2',
};

const ALL_EVENT_KEYS = [
  'shop_installed',
  'shop_uninstalled',
  'shop_closed',
  'shop_reopened',
  'shop_subscribed',
  'shop_unsubscribed',
];

const ALL_EVENT_LABELS = {
  shop_installed: 'Installed',
  shop_uninstalled: 'Uninstalled',
  shop_closed: 'Closed',
  shop_reopened: 'Reopened',
  shop_subscribed: 'Subscribed',
  shop_unsubscribed: 'Unsubscribed',
};

const INSTALL_VIEW_OPTIONS = [
  { label: 'Default view', value: 'default' },
  { label: 'Combined view', value: 'combined' },
  { label: 'Net grow', value: 'net_grow' },
];

const SUB_VIEW_OPTIONS = [
  { label: 'Default view', value: 'default' },
  { label: 'Combined view', value: 'combined' },
  { label: 'Net grow', value: 'net_grow' },
];

const toNumber = (v) => Number(v || 0);
const getPrimaryAxisBounds = (values = []) => {
  const parsed = values.map((value) => Number(value) || 0);
  const minValue = Math.min(...parsed, 0);
  const maxValue = Math.max(...parsed, 0);
  const range = Math.max(1, maxValue - minValue);
  const posPadding = Math.max(1, range * 0.12);
  const negPadding = Math.max(0.5, range * 0.08);
  return {
    min: Math.min(0, minValue - negPadding),
    max: Math.max(0, maxValue + posPadding),
  };
};

const getAlignedAxisBounds = (values = [], zeroRatio = 0.5) => {
  const parsed = values.map((value) => Number(value) || 0);
  const minValue = Math.min(...parsed, 0);
  const maxValue = Math.max(...parsed, 0);
  const clampedZeroRatio = Math.min(0.95, Math.max(0.05, zeroRatio));
  const spanByMax = maxValue <= 0 ? 1 : maxValue / (1 - clampedZeroRatio);
  const spanByMin = minValue >= 0 ? 0 : Math.abs(minValue) / clampedZeroRatio;
  const span = Math.max(1, spanByMax, spanByMin);
  return {
    min: -clampedZeroRatio * span,
    max: (1 - clampedZeroRatio) * span,
  };
};

const baseChartOptions = ({
  categories,
  stacked,
  yTitle,
  colors,
  barSeriesCount,
  dataLabelSeriesIndices,
  showDataLabels,
  lineCurve,
  chartHeight,
  yAxes,
}) => ({
  chart: {
    type: 'line',
    height: chartHeight,
    stacked: !!stacked,
    toolbar: {
      show: true,
      export: {
        png: {
          scale: 6,
          width: 2400,
        },
      },
    },
  },
  colors,
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: '60%',
      borderRadius: 3,
    },
  },
  dataLabels: {
    enabled: !!showDataLabels,
    enabledOnSeries: dataLabelSeriesIndices || Array.from({ length: barSeriesCount }, (_, idx) => idx),
    formatter: (val) => (Number(val) === 0 ? '' : String(val)),
    offsetY: -6,
    style: {
      fontSize: '11px',
      colors: ['#595959'],
    },
  },
  stroke: {
    width: Array.from({ length: colors.length }, (_, idx) => (idx < barSeriesCount ? 0 : 3)),
    curve: lineCurve || 'straight',
  },
  xaxis: { categories },
  yaxis: yAxes || { title: { text: yTitle || 'Events' } },
  legend: {
    show: true,
    position: 'top',
  },
  tooltip: {
    shared: true,
    intersect: false,
  },
});

const AppEventsChartSection = ({ appId, dateRange, granularity, chartSettings, shopifyPlan, country }) => {
  const [installMode, setInstallMode] = useState('default');
  const [subMode, setSubMode] = useState('default');

  const dateStart = dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : undefined;
  const dateEnd = dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : undefined;

  const { data, isFetching } = useQuery(
    ['app-events-chart', appId, dateStart, dateEnd, granularity, shopifyPlan, country],
    () =>
      getAppEventsChart({
        appId,
        dateStart,
        dateEnd,
        granularity,
        ...(shopifyPlan ? { shopifyPlan } : {}),
        ...(country ? { country } : {}),
      }),
    { keepPreviousData: true, enabled: !!appId && !!dateStart && !!dateEnd }
  );

  const buckets = useMemo(() => data?.buckets || [], [data?.buckets]);
  const categories = useMemo(() => buckets.map((b) => b.bucket), [buckets]);

  const allEventsSeries = useMemo(
    () =>
      ALL_EVENT_KEYS.map((key) => ({
        name: ALL_EVENT_LABELS[key] || key,
        type: 'bar',
        data: buckets.map((b) => toNumber(b?.allEvents?.[key])),
      })),
    [buckets]
  );

  const allEventsColors = useMemo(
    () => [
      EVENT_COLORS.shop_installed,
      EVENT_COLORS.shop_uninstalled,
      EVENT_COLORS.shop_closed,
      EVENT_COLORS.shop_reopened,
      EVENT_COLORS.shop_subscribed_upgrade,
      EVENT_COLORS.shop_unsubscribed,
    ],
    []
  );

  const installSeries = useMemo(() => {
    if (installMode === 'combined') {
      return [
        {
          name: 'Install',
          type: 'bar',
          group: 'acquire',
          data: buckets.map((b) => toNumber(b?.views?.install?.stacked?.install)),
        },
        {
          name: 'Reopen',
          type: 'bar',
          group: 'acquire',
          data: buckets.map((b) => toNumber(b?.views?.install?.stacked?.reopen)),
        },
        {
          name: 'Uninstall',
          type: 'bar',
          group: 'churn',
          data: buckets.map((b) => toNumber(b?.views?.install?.stacked?.uninstall)),
        },
        {
          name: 'Close',
          type: 'bar',
          group: 'churn',
          data: buckets.map((b) => toNumber(b?.views?.install?.stacked?.close)),
        },
      ];
    }

    if (installMode === 'net_grow') {
      return [
        {
          name: 'Net grow',
          type: 'bar',
          data: buckets.map((b) => toNumber(b?.views?.install?.net?.netGrow)),
        },
        {
          name: 'Cumulative grow',
          type: 'line',
          yAxisIndex: 1,
          data: buckets.map((b) => toNumber(b?.views?.install?.net?.cumulativeGrow)),
        },
      ];
    }

    return [
      { name: 'Install', type: 'bar', data: buckets.map((b) => toNumber(b?.views?.install?.separated?.install)) },
      { name: 'Reopen', type: 'bar', data: buckets.map((b) => toNumber(b?.views?.install?.separated?.reopen)) },
      {
        name: 'Uninstall',
        type: 'bar',
        data: buckets.map((b) => toNumber(b?.views?.install?.separated?.uninstall)),
      },
      { name: 'Close', type: 'bar', data: buckets.map((b) => toNumber(b?.views?.install?.separated?.close)) },
    ];
  }, [buckets, installMode]);

  const subSeries = useMemo(() => {
    if (subMode === 'combined') {
      return [
        {
          name: 'New sub',
          type: 'bar',
          group: 'positive',
          data: buckets.map((b) => toNumber(b?.views?.subscription?.stacked?.new_sub)),
        },
        {
          name: 'Sub upgrade',
          type: 'bar',
          group: 'positive',
          data: buckets.map((b) => toNumber(b?.views?.subscription?.stacked?.sub_upgrade)),
        },
        {
          name: 'Sub downgrade',
          type: 'bar',
          group: 'negative',
          data: buckets.map((b) => toNumber(b?.views?.subscription?.stacked?.sub_downgrade)),
        },
        {
          name: 'Unsub',
          type: 'bar',
          group: 'negative',
          data: buckets.map((b) => toNumber(b?.views?.subscription?.stacked?.unsub)),
        },
      ];
    }

    if (subMode === 'net_grow') {
      return [
        {
          name: 'Net grow',
          type: 'bar',
          data: buckets.map((b) => toNumber(b?.views?.subscription?.net?.netGrow)),
        },
        {
          name: 'Cumulative grow',
          type: 'line',
          yAxisIndex: 1,
          data: buckets.map((b) => toNumber(b?.views?.subscription?.net?.cumulativeGrow)),
        },
      ];
    }

    return [
      { name: 'New sub', type: 'bar', data: buckets.map((b) => toNumber(b?.views?.subscription?.separated?.new_sub)) },
      {
        name: 'Sub upgrade',
        type: 'bar',
        data: buckets.map((b) => toNumber(b?.views?.subscription?.separated?.sub_upgrade)),
      },
      {
        name: 'Sub downgrade',
        type: 'bar',
        data: buckets.map((b) => toNumber(b?.views?.subscription?.separated?.sub_downgrade)),
      },
      { name: 'Unsub', type: 'bar', data: buckets.map((b) => toNumber(b?.views?.subscription?.separated?.unsub)) },
    ];
  }, [buckets, subMode]);

  const installNetAxis = useMemo(() => {
    const netValues = buckets.map((b) => toNumber(b?.views?.install?.net?.netGrow));
    const cumulativeValues = buckets.map((b) => toNumber(b?.views?.install?.net?.cumulativeGrow));
    const net = getPrimaryAxisBounds(netValues);
    const zeroRatio = Math.abs(net.min) / (net.max - net.min || 1);
    return {
      net,
      cumulative: getAlignedAxisBounds(cumulativeValues, zeroRatio),
    };
  }, [buckets]);

  const subNetAxis = useMemo(() => {
    const netValues = buckets.map((b) => toNumber(b?.views?.subscription?.net?.netGrow));
    const cumulativeValues = buckets.map((b) => toNumber(b?.views?.subscription?.net?.cumulativeGrow));
    const net = getPrimaryAxisBounds(netValues);
    const zeroRatio = Math.abs(net.min) / (net.max - net.min || 1);
    return {
      net,
      cumulative: getAlignedAxisBounds(cumulativeValues, zeroRatio),
    };
  }, [buckets]);

  const installColors = useMemo(() => {
    if (installMode === 'net_grow') {
      return [EVENT_COLORS.netGrow, EVENT_COLORS.cumulativeGrow];
    }
    return [
      EVENT_COLORS.shop_installed,
      EVENT_COLORS.shop_reopened,
      EVENT_COLORS.shop_uninstalled,
      EVENT_COLORS.shop_closed,
    ];
  }, [installMode]);

  const subColors = useMemo(() => {
    if (subMode === 'net_grow') {
      return [EVENT_COLORS.netGrow, EVENT_COLORS.cumulativeGrow];
    }
    return [
      EVENT_COLORS.shop_installed,
      EVENT_COLORS.shop_subscribed_upgrade,
      EVENT_COLORS.shop_subscribed_downgrade,
      EVENT_COLORS.shop_unsubscribed,
    ];
  }, [subMode]);

  const renderChart = ({
    series,
    stacked = false,
    yTitle = 'Events',
    colors,
    barSeriesCount = 4,
    yAxes,
    localLineCurve,
    dataLabelSeriesIndices,
  }) => (
    <Chart
      options={baseChartOptions({
        categories,
        stacked,
        yTitle,
        colors,
        barSeriesCount,
        dataLabelSeriesIndices,
        showDataLabels: chartSettings?.showDataLabels,
        lineCurve: localLineCurve || chartSettings?.lineCurve,
        chartHeight: chartSettings?.height || 360,
        yAxes,
      })}
      series={series}
      type="line"
      height={chartSettings?.height || 360}
    />
  );

  return (
    <Card title="Event charts" style={{ marginTop: 16 }} loading={isFetching}>
      {!buckets.length ? (
        <Text type="secondary">No chart data in selected date range.</Text>
      ) : (
        <Tabs defaultActiveKey="all-events">
          <TabPane tab="All events" key="all-events">
            {renderChart({
              series: allEventsSeries,
              stacked: false,
              yTitle: 'Total events',
              colors: allEventsColors,
              barSeriesCount: 6,
              localLineCurve: chartSettings?.lineCurve,
            })}
          </TabPane>

          <TabPane tab="Install / Uninstall" key="install-uninstall">
            <div style={{ marginBottom: 12 }}>
              <Segmented options={INSTALL_VIEW_OPTIONS} value={installMode} onChange={setInstallMode} />
            </div>
            {renderChart({
              series: installSeries,
              stacked: installMode === 'combined',
              yTitle: 'Install/Uninstall',
              colors: installColors,
              barSeriesCount: installMode === 'net_grow' ? 1 : 4,
              yAxes:
                installMode === 'net_grow'
                  ? [
                      { title: { text: 'Net grow' }, min: installNetAxis.net.min, max: installNetAxis.net.max },
                      {
                        opposite: true,
                        title: { text: 'Cumulative grow' },
                        min: installNetAxis.cumulative.min,
                        max: installNetAxis.cumulative.max,
                      },
                    ]
                  : undefined,
              localLineCurve: chartSettings?.lineCurve,
              dataLabelSeriesIndices: installMode === 'net_grow' ? [0, 1] : undefined,
            })}
          </TabPane>

          <TabPane tab="Subscribe / Unsubscribe" key="sub-unsub">
            <div style={{ marginBottom: 12 }}>
              <Segmented options={SUB_VIEW_OPTIONS} value={subMode} onChange={setSubMode} />
            </div>
            {renderChart({
              series: subSeries,
              stacked: subMode === 'combined',
              yTitle: 'Subscription',
              colors: subColors,
              barSeriesCount: subMode === 'net_grow' ? 1 : 4,
              yAxes:
                subMode === 'net_grow'
                  ? [
                      { title: { text: 'Net grow' }, min: subNetAxis.net.min, max: subNetAxis.net.max },
                      {
                        opposite: true,
                        title: { text: 'Cumulative grow' },
                        min: subNetAxis.cumulative.min,
                        max: subNetAxis.cumulative.max,
                      },
                    ]
                  : undefined,
              localLineCurve: chartSettings?.lineCurve,
              dataLabelSeriesIndices: subMode === 'net_grow' ? [0, 1] : undefined,
            })}
          </TabPane>
        </Tabs>
      )}
    </Card>
  );
};

export default AppEventsChartSection;
